import { Injectable, OnDestroy } from "@angular/core";
import {
  LedConfiguration,
  RelayCloseConfiguration,
  RelayOpenConfiguration,
  ReponseObject,
  SiboHandlerStub,
} from "./mocks/sibo-handler.stub";
import { Store } from "@ngrx/store";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import { from, interval, Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";

import {
  distinctUntilKeyChanged,
  filter,
  map,
  take,
  tap,
  timeout,
} from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { BIGInputNativeAdapter } from "src/model/class/general classes/big-input-native-adapter";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { Plant } from "src/model/class/general classes/plant";
import { Settings } from "src/model/class/settings";
import { ABBMilleniumTag } from "src/model/class/tag classes/abb-millenium-tag";
import { BIGTag } from "src/model/class/tag classes/big-tag";
import { GenericTag } from "src/model/class/tag classes/generic-tag";
import { VimarTag } from "src/model/class/tag classes/vimar-tag";
import { Colors } from "src/model/enum/enumColor";
import {
  BIGDevice,
  BIGDeviceAddress,
  BIGDeviceRelayOperationKind,
  BIGDeviceRelaysQ896S,
  BIGDeviceType,
} from "src/components/shared-components/enum/enumDevice";
import { GatewayKind } from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { iDevice } from "src/model/interface/iDevice";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { iSiboInfo } from "src/model/interface/iSiboInfo";
import { SiboDeviceInfoState } from "src/model/interface/sibo-device-info-state";
import {
  clientSettingsUpdateDeviceNameSettingAction,
  clientSettingsUpdateMaximumRelayActivationTimeSettingListAction,
  clientSettingsUpdateRelayTimeLimitEnablersListAction,
  clientSettingsUpdateSiboDeviceIDAction,
} from "src/state/actions/client-settings.actions";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { listGateway } from "src/state/selectors/gateway.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { AddressService } from "./address.service";
import { DeviceService } from "./device.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { TagService } from "./tag.service";
import { blackBoxAddEntry } from "src/state/actions/black-box.actions";
import { BlackBoxEntry } from "src/model/class/general classes/black-box-entry";
import { ExtraInfoBlackBox } from "src/model/class/general classes/extra-info-black-box";
import { BlackBoxDeviceEvent } from "src/model/enum/enumBlackBox";
import { ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";

@Injectable({
  providedIn: "root",
})
export class BIGService implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "BIG.service";
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectClientSettingsUserSub: Subscription = null;
  private _selectedPlantSubscription: Subscription = null;
  //private _selectGatewayListSubscription: Subscription = null;
  private _input1ChangeSubscription: Subscription = null;
  private _input2ChangeSubscription: Subscription = null;
  private _serverSettingSubscription: Subscription = null;
  //#endregion

  private _subscriptionAddressUpdate: Subscription = null;
  /**List of address objects associated to mqtt-addresses we need to check the last value of at Subscription moment, since to this end we need to handle "Address" objects taken from the server*/
  private _addressTocCheck: Array<Address> = null;

  private _errorInGetAddressINPUT_1_DISABLEapi: boolean = false;
  private _errorInGetAddressINPUT_2_DISABLEapi: boolean = false;
  private _isCheckingApisInProgress: boolean = false;

  private _LedColor: string = "";
  private _R1PermanentOnOff: boolean = false;
  private _R2PermanentOnOff: boolean = false;
  private _R1StairCaseOn: number = 0;
  private _R2StairCaseOn: number = 0;
  private _R1OnDelay: number = 0;
  private _R1OffDelay: number = 0;
  private _R2OnDelay: number = 0;
  private _R2OffDelay: number = 0;
  private _input1Disable: boolean = true;
  private _input2Disable: boolean = true;

  private _currentClientSetting: ClientSettings = null;
  private _serverSetting: Settings = null;
  private _mqttGateway: Gateway = null;
  private _currentPlant: Plant = null;

  private _input1NativeAdapter: BIGInputNativeAdapter = null;
  private _input2NativeAdapter: BIGInputNativeAdapter = null;

  /**
   * This flag is used to avoid the "double reading" problem from NFC module. If enough time has passed from the invocation of the 
   * Sibo-plugin reading method (readBlock()/readUID()) that the next reading can be considered "admissible", iy is set to TRUE.
   * Orìtherwise, it is set to FALSE.
   */
  private _isNFCReadyToRelease: boolean = false;

  //#region Private properties; useful to handle operations targeted to single device-integrated relays.
  //These properties are implemented as arrays, where each item contains data referring to a single relay. The leght of the arrays are set
  //at initialisation of service and it is defined by the leght of the "BIGDeviceRelays****" enum formalizing the relay set present on the device.
  //
  //* ATTENTION: Since the first key of a "BIGDeviceRelays****" enum represents the "UNDEFINED"/"NO_RELAY" situation, the first significative item in these array shall be
  // the SECOND one (index= 1, as in "Relay 1").

  /**This array specifies the last "relay-handling" command issued to a peculiar relay.*/
  private _lastCommandToRelay: Array<BIGDeviceRelayOperationKind> = null;
  /**This array  specifies if the  "safety time limit" is currently ACTIVE for a peculiar relay. This means the associated "automatic opening" timer is yet to expire.*/
  private _relayIsUnderSafety: Array<boolean> = null;
  /**This array  specifies the extension of the "safety time limit" to be applied to a peculiar relay. */
  private _safetyTimers: Array<any> = null;
  //#endregion

  private _enumDeviceRelays = BIGDeviceRelaysQ896S;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  _eventSubscriptions: Array<Subscription> = null;
  //
  /** Used for references gateway (MQTT) for address */

  public idDevice: number = -1;
  public deviceInfo: iDevice = null;
  public deviceModel: BIGDevice = BIGDevice.Unknown;
  public isMyToDevice: boolean = false;
  public isQ5Model: boolean = false;
  public isQ896SModel: boolean = false;
  public isInitialized: boolean = false;
  public connectionResponseObject: ReponseObject = null;

  constructor(
    private _siboHandler: SiboHandlerStub,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _deviceService: DeviceService,
    private _store: Store,
    private _mqtt: MqttClientService,
    private _addressService: AddressService,
    private _tagService: TagService,
    private _mqttServiceNative: MqttService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeSettingServiceData();
    this.subscribeEvents();
    this.loadCurrentPlant();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeToMQTTAddresses();

    this.clearAllRelay();
    this._mqttGateway = null;

    this.unsubscribeStoreSelect();

    if (this._subscriptionAddressUpdate) {
      this._subscriptionAddressUpdate.unsubscribe();
      this._subscriptionAddressUpdate = null;
    }
    this._addressTocCheck = null;
    this._errorInGetAddressINPUT_1_DISABLEapi = false;
    this._errorInGetAddressINPUT_2_DISABLEapi = false;
    this._isCheckingApisInProgress = false;
    this._serverSettingSubscription?.unsubscribe();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this.loadServerSettings();
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "subscribeEvents()",
          "REACT TO OnLoginPlantDid - START",
        );
        // SiboHandler removed: see app.component.ts comment.
        // await this.initializeSiboVariableDevice();

        if (this.isMyToDevice) {
          const idDevice: number = await this.getDeviceInfo(this.deviceInfo);

          this._store.dispatch(
            clientSettingsUpdateSiboDeviceIDAction({
              idDevice,
            })
          );

          await this.loadSiboAddress();
          await this.dispatchOperatingMode();
          await this.dispatchCurrentDeviceModel();
        }
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "START"
        ); //

        if (this.isMyToDevice) {
          this.changeColorLedWithString(Colors.NONE);
          this.clearAllRelay();

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 1: "
          );
          for (const entry of Array.from(
            this._mqttAddressSubscriptionsMap.entries()
          )) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_eventService",
              "OnMakeLogout",
              "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
            );
          }

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 1."
          );

          await this.unsubscribeToMQTTAddresses();

          this.disableInputTransmission();

          this.resetInputVariable();
          this.unsubscribeToInputChanges();

          this.clearAllRelay();
        }

        if (this._subscriptionAddressUpdate) {
          this._subscriptionAddressUpdate.unsubscribe();
          this._subscriptionAddressUpdate = null;
        }
        this._addressTocCheck = null;
        this._errorInGetAddressINPUT_1_DISABLEapi = false;
        this._errorInGetAddressINPUT_2_DISABLEapi = false;
        this._isCheckingApisInProgress = false;

        this._mqttGateway = null;
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "TERMINATED"
        );

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "LOCAL MAP-SUBSCRIPTIONS 2: "
        );
        for (const entry of Array.from(
          this._mqttAddressSubscriptionsMap.entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
          );
        }

        //DEBUGGING

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 2."
        );
        for (const entry of Array.from(
          new Map(Object.entries(this._mqttServiceNative.observables)).entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 2 Entry:  " + entry[0]
          );
        }
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Load server settings
   */
  public loadServerSettings(): void {
    this._serverSettingSubscription = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        this._serverSetting = settings;
      });
  }

  /**
   * Subscribe to gateway list for retrive mqtt gateway
   */
  private async loadSiboAddress(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "loadSiboAddress()",
      "START"
    );

    await this.waitForSubscriptionToGatewayStore();

    if (this._mqttGateway !== null) {
      this.subscribeMQTTAddresses();
    }
  }

  /**
   * This method handles the puntual read (from Store) of "mqttGateway"-related data from "listGateway" feature.
   * @returns A Promise object containing "mqttGateway"-related data. This object is used in order to execute functions which needs
   * these data ONLY when the data have been already obtained.
   */
  private async waitForSubscriptionToGatewayStore(): Promise<Gateway> {
    return this._store
      .select(listGateway)
      .pipe(
        filter((listGatewayObject: GenericStoreApiData) => !!listGatewayObject),
        filter(
          (listGatewayObject: GenericStoreApiData) => listGatewayObject?.loaded
        ),
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            !(
              listGatewayStore.errorGeneric ||
              listGatewayStore.errorNotYetImplemented
            )
        ),
        tap((listGatewayStore: GenericStoreApiData) => {
          if (listGatewayStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listGateway)",
              "subscribeToGatewayStore()",
              "We are using old data!!"
            );
          }
        }),
        map(
          (listGatewayObject: GenericStoreApiData) =>
            listGatewayObject.data?.data || []
        ),
        map((gatewayList: Array<Gateway>) => {
          return gatewayList.find(
            (gateway) => gateway.kind === GatewayKind.MQTT
          );
        }),
        filter((mqttGateway: Gateway) => mqttGateway !== null),

        tap((mqttGateway: Gateway) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_gatewayService.gatewayList",
            "subscribeToGatewayStore()",
            "Subscribe to mqtt-addresses"
          );
          this._mqttGateway = mqttGateway;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Read data for ABB Millenium strategy by MyTo reader
   *
   * @param blockIndex Block index to read
   * @param enableTimeout Check it timeout is enabled
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   * @returns Readed tag of ABBMilleniumTag
   */
  public async readBlockABBMillenium(
    blockIndex: number,
    enableTimeout: boolean,
    callStamp: string
  ): Promise<ABBMilleniumTag> {
    const block: ABBMilleniumTag = new ABBMilleniumTag();
    this._isNFCReadyToRelease = false;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "readBlockABBMillenium()",
        "ReadBlockDevice try to read: " + blockIndex + " | STAMP: " + callStamp,
      );
      let response: ReponseObject = null;
      if (enableTimeout) {
        response = await from(
          this._siboHandler.ReadBlock(blockIndex, callStamp)
        )
          .pipe(timeout(10000))
          .toPromise();
      } else {

        this.prematureReleaseTimer(callStamp);
        while (!this._isNFCReadyToRelease) {
          response = await this._siboHandler.ReadBlock(blockIndex, callStamp);

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "readBlockABBMillenium()",
            "IN CYCLE - AFTER READBLOCK. Go ON? " + this._isNFCReadyToRelease,
          );
        }

      }

      if (response.code === 0) {
        let result = response.data
          .toString()
          .replace(/ /g, "")
          .substring(18, 50);
        block.unpack(result, "");
      } else {
        //Errore
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          " readBlockABBMillenium() ",
          "ERROR in _siboHandler.ReadBlock(...) response: " + response
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        " readBlockABBMillenium() ",
        "ERROR in cathc: " + error.message
      );
    }
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "readBlockABBMillenium()",
      "ReadBlockDevice END! | STAMP: " + callStamp,
    );

    return block;
  }

  /**
   * Stop reading process of UID or card number
   * It's supported only for 5 and 7 inches Myto
   *
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   */
  public async StopReading(callStamp: string): Promise<void> {

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "StopReading()",
      "Start stopping | STAMP: " + callStamp,
    );

    await this._siboHandler.StopReading(callStamp);

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "StopReading()",
      "Stopped | STAMP: " + callStamp,
    );
  }


  /**
   * Read data for Vimar Well Contact Plus strategy by MyTo reader
   *
   * @param blockIndex Block index to read
   * @param enableTimeout Check it timeout is enabled
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   * @returns Readed tag of ABBMilleniumTag
   */
  public async readBlockVimarWCS(blockIndex: number, enableTimeout: boolean, callStamp: string): Promise<VimarTag> {
    const block: VimarTag = new VimarTag();

    this._isNFCReadyToRelease = false;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "readBlockVimarWCS()",
        "Try to read: " +
          blockIndex +
          " | Timeout: " +
          enableTimeout +
          " | STAMP: " +
          callStamp,
      );
      let response: ReponseObject = null;
      if (enableTimeout) {
        response = await from(this._siboHandler.ReadBlock(blockIndex, callStamp))
          .pipe(timeout(10000))
          .toPromise();
      } else {

        this.prematureReleaseTimer(callStamp);
        while (!this._isNFCReadyToRelease) {
          response = await this._siboHandler.ReadBlock(blockIndex, callStamp);

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "readBlockVimarWCS()",
            "IN CYCLE - AFTER READBLOCK. Go ON? " + this._isNFCReadyToRelease,
          );
        }

      }

      if (response.code === 0) {
        let result = response.data
          .toString()
          .replace(/ /g, "")
          .substring(18, 50);
        block.unpack(result, "");
      } else {
        //Errore
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          " readBlockVimarWCS() ",
          "ERROR in _siboHandler.ReadBlock(...) response: " + response
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        " readBlockVimarWCS() ",
        "ERROR in cathc: " + error.message
      );
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "readBlockVimarWCS()",
      "ReadBlockDevice END! | STAMP: " + callStamp,
    );

    return block;
  }

  /**
   * Read block of NFC Tag based on strategy (ABB/Vimar or Myto) and specific timeout
   *
   * @param enableTimeout  Check if timeout is enable
   * @param deviceType Device strategy to read data
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   * @returns
   */
  public async readBlock(
    enableTimeout: boolean = true,
    deviceType: BIGDeviceType,
    callStamp: string
  ) {

    let block: GenericTag = null;
    let blockIndex: number =
      this._tagService.calculateBlockIndexForMytoByDeviceType(deviceType);
    if (deviceType == BIGDeviceType.ABB_MILLENIUM) {
      block = await this.readBlockABBMillenium(
        blockIndex,
        enableTimeout,
        callStamp
      );
    } else if (deviceType == BIGDeviceType.VIMAR_WELL_CONTACT_PLUS) {
      block = await this.readBlockVimarWCS(blockIndex, enableTimeout, callStamp);
    } else if (deviceType == BIGDeviceType.BIG_MIFARE) {
      block = await this.readBlockBIGMifare(blockIndex, enableTimeout, callStamp);
    }


    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "readBlock()",
      "END. Read: " + JSON.stringify(block),
    );

    return block;
  }

  public async readBlockBIGMifare(blockIndex: number, enableTimeout: boolean, callStamp: string) {
    const block: BIGTag = new BIGTag();
    this._isNFCReadyToRelease = false;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "readBlockBIGMifare()",
        "Try to read: " + blockIndex,
      );
      let response: ReponseObject = null;
      if (enableTimeout) {
        response = await from(this._siboHandler.ReadBlock(blockIndex, ""))
          .pipe(timeout(10000))
          .toPromise();
      } else {

        this.prematureReleaseTimer(callStamp);
        while (!this._isNFCReadyToRelease) {
          response = await this._siboHandler.ReadBlock(blockIndex, "");

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "readBlockBIGMifare()",
            "IN CYCLE - AFTER READBLOCK. Go ON? " + this._isNFCReadyToRelease,
          );
        }

      }


      if (response.code === 0) {
        let result = response.data
          .toString()
          .replace(/ /g, "")
          .substring(18, 50);
        block.unpack(result, "");
      } else {
        //Errore
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          " readBlockBIGMifare() ",
          "ERROR in _siboHandler.ReadBlock(...) response: " + response
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        " readBlockBIGMifare() ",
        "ERROR in cathc (from _siboHandler.ReadBlock): " + error.message
      );
    }
    return block;
  } //

  /**
   * Load key to be passed down to the NFC module controlling the NFC-feature of the MYTO device. This key is then used to define which blocks on the card
   * should be read, and how.
   *
   * @param deviceType Type of the card "manufacturer"; it is used to retrieve the correct key.
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   * @returns
   */
  public async loadKey(deviceType: BIGDeviceType, callStamp: string) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "loadKey()",
      "START! | STAMP: " + callStamp,
    );

    let key = this._tagService.getKeyByDeviceType(deviceType);

    const loadKeyReturn = await this._siboHandler.LoadKey(key, callStamp);

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "loadKey()",
      "LoadKey return: " + loadKeyReturn + " | STAMP: " + callStamp,
    );
    return loadKeyReturn;
  }

  /**
   * Read UID
   *
   * @param callStamp DEBUG FEATURE. Inserted into the log strings, allow us to identify a particular invocation of the method (and all the consequent actions) by specifing when it was called.
   */
  public async readUID(callStamp: string) {
    const block: BIGTag = new BIGTag();
    this._isNFCReadyToRelease = false;

    try {
      let response: ReponseObject = null;

      this.prematureReleaseTimer(callStamp);
      while (!this._isNFCReadyToRelease) {
        response = await this._siboHandler.ReadUID(callStamp);
      }

      if (response.code === 0) {
        let toRead = response.data.toString().replace(/ /g, "").length - 4;

        let result = response.data
          .toString()
          .replace(/ /g, "")
          .substring(16, toRead);

        block.unpackBlockUID(result);
      } else {
        //Errore
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          " readUID() ",
          "ERROR in _siboHandler.ReadUID(...) response: " + response
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        " readUID() ",
        "ERROR in catch: " + error.message
      );
    }
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "readUID()",
      "readUID END! | STAMP: " + callStamp,
    );

    return block;
  }

  /**
   * Set brightness for q5 with proximity sensor
   */
  public setProximitySensor(
    activate: boolean,
    time: number,
    perc: number,
    defaultPerc: number
  ) {
    this._siboHandler.SetProximitySensor(activate, time, perc, defaultPerc);
  }

  /**
   * Initialize all service variable reading from device plus storage
   */
  public async initializeSiboVariableDevice(): Promise<iSiboInfo> {
    let deviceName: string = "";

    if (!this.isInitialized) {
      try {
        //clientSetting = await this.loadClientSettingromStorage();

        if (this._currentClientSetting) {
          deviceName = this._currentClientSetting.deviceName;
        }

        const deviceInfoNative: iSiboInfo = await this.getSiboDeviceInfo(
          deviceName
        );

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "initializeSiboVariableDevice()",
          "isMyToDevice? " + this.isMyToDevice
        );

        this.isInitialized = true;

        return deviceInfoNative;
      } catch (exception) {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "initializeSiboVariableDevice",
          "Error: " + exception.message
        );
        this.isInitialized = true;

        throw new Error(exception.message);
      }
    }

    return null;
  }

  /**
   * AB: function used to create/update a new device, return device id
   * @param device model for body request, it contain device information
   */
  public async getDeviceInfo(deviceInfo: iDevice): Promise<any> {
    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "getDeviceInfo()",
        "Device INFO to be passed: " + JSON.stringify(deviceInfo),
      );

      const returnSiboDevice: SiboDeviceInfoState =
        await this._deviceService.getBIGDeviceInfo(deviceInfo);

      this.idDevice = returnSiboDevice.id;

      if (this.deviceModel === BIGDevice.Q896S) {
        this.setInitialColorLedAddress(returnSiboDevice);
      }

      return this.idDevice;
    } catch (exception) {
      throw new Error(exception);
    }
  }

  /**
   * Handle show/hide navite status bar
   *
   * @param showStatusBar  Hide/show native status bar
   */
  public async showNativeStatusNavigationBar(
    showStatusBar: boolean
  ): Promise<void> {
    await this._siboHandler.ShowStatusBar({ showStatusBar });
  }

  /**
   * Set brightness for inactivity
   * @param percentageBrigh
   */
  public async setBrightnessInactivity(percentageBrigh: number) {
    await this._siboHandler.SetBrightness(percentageBrigh);
  }

  /**
   * Handle native reboot device
   */
  public async rebootDevice(): Promise<void> {
    await this._siboHandler.RebootDevice();
  }

  /**
   * Change LED SIbo color
   *
   * @param red red channel value (0 - 255)
   * @param green green channel value (0 - 255)
   * @param blue blue channel value (0 - 255)
   */
  public async changeColorLed(red: number, green: number, blue: number) {
    const ledColorConfig: LedConfiguration = { red, green, blue };

    try {
      return await this._siboHandler.ChangeLEDColor(ledColorConfig);
    } catch (exception) {
      return Promise.resolve(null);
    }
  }

  /**
   * Change LED SIbo color with string led color parameter
   *
   * @param ledColor Color expressed in decimal format (es. #d3d3d3)
   */
  public changeColorLedWithString(ledColor: string): void {
    switch (ledColor.toUpperCase()) {
      case Colors.RED:
        this.changeColorLed(255, 0, 0);
        break;
      case Colors.GREEN:
        this.changeColorLed(0, 255, 0);
        break;
      case Colors.BLUE:
        this.changeColorLed(0, 0, 255);
        break;
      case Colors.NONE:
        this.changeColorLed(0, 0, 0);
        break;
      default:
        break;
    }
  }

  /**
   * Close Sibo relay
   *
   * @param relayNumber relay number (1 or 2 - if installed)
   * @param onDelay on delay command (milliseconds)
   * @param stairCaseDuration staircase time duration (millisecond)
   */
  public async closeRelay(
    relayNumber: BIGDeviceRelaysQ896S,
    onDelay: number,
    stairCaseDuration: number
  ) {
    const closeConfiguration: RelayCloseConfiguration = {
      relayNumber,
      onDelay,
      stairCaseDuration,
    };
    try {
      return this.handleSafetyMaximumTimeLimit(
        BIGDeviceRelayOperationKind.CLOSE,
        closeConfiguration
      );
    } catch (exception) {
      throw new Error(exception);
    }
  }

  /**
   * Open Sibo relay
   *
   * @param relayNumber number of relay (1 or 2)
   * @param offDelay off delay command (millisecond)
   */
  public async openRelay(relayNumber: BIGDeviceRelaysQ896S, offDelay: number) {
    const openConfiguration: RelayOpenConfiguration = { relayNumber, offDelay };

    try {
      return this.handleSafetyMaximumTimeLimit(
        BIGDeviceRelayOperationKind.OPEN,
        openConfiguration
      );
    } catch (exception) {
      throw new Error(exception);
    }
  }

  /**
   * AB: function used to get device information with plugin
   */
  private async getSiboDeviceInfo(forceDeviceName: string = "") {
    let deviceDescriptor: iSiboInfo = null;

    this.isMyToDevice = false;

    try {
      deviceDescriptor = await this.readDeviceDescriptor();

      this.isMyToDevice = this.checkIfMyToDevice(deviceDescriptor);
      this.isQ5Model = this.checkIfQ5Model(deviceDescriptor);
      this.isQ896SModel = this.checkIfQ896SModel(deviceDescriptor);


      //NOTA: Utilizzare il deviceDescriptor per discriminare tra diversi modelli di "SiboDevice" (5'' VS 7'')
      //ALSO: Cambiare nome variabile "isSiboDevice" ---> "isMyToDevice"

      // Check if SIBO Device
      if (this.isMyToDevice) {
        /** Resaving device name if is equal to empty string */
        if (forceDeviceName === "") {
          forceDeviceName = deviceDescriptor.model;

          await this.setDeviceNameSetting(forceDeviceName);
        }

        await this.handleSiboDevice(deviceDescriptor, forceDeviceName);

        return deviceDescriptor;
      } else {
        return null;
      }
    } catch (exception) {
      return Promise.reject();
    }
  }

  /**
   * Check if device is MyTo family from device descriptor
   *
   * @param deviceDescriptor device descriptor readed from device (lower level api)
   */
  protected checkIfMyToDevice(deviceDescriptor: iSiboInfo): boolean {
    if (deviceDescriptor) {
      if (deviceDescriptor.isSiboDevice) {
        return (
          deviceDescriptor.isSiboDevice.toString().toUpperCase() === "TRUE"
        );
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  protected checkIfQ5Model(deviceDescriptor: iSiboInfo): boolean {
    if (deviceDescriptor) {
      if (deviceDescriptor.model) {
        return deviceDescriptor.model.toString().toUpperCase() === "Q5";
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  protected checkIfQ896SModel(deviceDescriptor: iSiboInfo): boolean {
    if (deviceDescriptor) {
      if (deviceDescriptor.model) {
        return deviceDescriptor.model.toString().toUpperCase() === "Q896S";
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Handle sibo device generic operation: setup device info, connect with it plugin
   *  and turn off led
   *
   * @param deviceDescriptor Device info readed from lower lever API
   * @param forceDeviceName forced name
   */
  protected async handleSiboDevice(
    deviceDescriptor: iSiboInfo,
    forceDeviceName = ""
  ) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleSiboDevice()",
      "START"
    );

    try {
      this.deviceModel = EnumStringConverter.deviceModelEnumAsString(
        deviceDescriptor.model
      );

      this.deviceInfo = {
        Model: deviceDescriptor.model,
        Name: forceDeviceName,
        SerialNumber: deviceDescriptor.serialNumber,
      };
      this._loggerService.deviceInfo = this.deviceInfo;

      this.connectionResponseObject = await this._siboHandler.Connect();

      /** turn off red LED color (on 7" inch devices is turn on on startup) */
      await this.changeColorLed(0, 0, 0);
    } catch (exception) {
      this.connectionResponseObject = exception;
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "handleSiboDevice()",
        "Error: " + exception.message
      );
      Promise.reject();
    }
  }

  /**
   * Reading device descriptor info from lower API (plugin-sibo-device)
   */
  private async readDeviceDescriptor() {
    let deviceDescriptor: iSiboInfo = null;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "readDeviceDescriptor()",
        "Invoking CheckCompatibility()",
      );
      const response: ReponseObject =
        await this._siboHandler.CheckCompatibility();

      deviceDescriptor = JSON.parse(response.data.toString());
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "readDeviceDescriptor()",
        "ERROR message: " + exception.message
      );
    }

    return deviceDescriptor;
  }

  /**
   *  AB: support function used to generate COMPLETE access control addresses.
   * @param property subGroup of the access control address
   * @returns A promise with a string, detailig the COMPLETE address. This address follows the pattern:
   *
   *    "instancdeGUIID" + "_BIG/"+ "device id" + "/" + "subgroup"
   */
  public async getVirtualAccessInstanceGUIDAddress(
    property: BIGDeviceAddress
  ): Promise<string> {
    if (this._mqttGateway === null) {
      await this.waitForSubscriptionToGatewayStore();
    }

    return (
      this._mqttGateway.instanceGUID +
      "_" +
      this.getVirtualAccessAddress(property)
    );
  }

  /**
   * Get BIG device address by specific property
   *
   * @param property Property to get address
   * @returns
   */
  public getVirtualAccessAddress(property: BIGDeviceAddress): string {
    return "BIG/" + this.idDevice + "/" + property;
  }

  /**
   * support function used to generate SIMPLE access control addresses, IE without the prefix detailing the instanceGUIid
   * @param completeVirtualAccessAddress
   * @returns a string, detailig the SIMPLE address. This address follows the pattern:
   *
   *    "BIG/"+ "device id" + "/" + "subgroup"
   */
  private getSimpleVirtualAccessAddress(
    completeVirtualAccessAddress: string
  ): string {
    return completeVirtualAccessAddress.slice(
      completeVirtualAccessAddress.indexOf("_") + 1
    );
  }

  /**
   * Set device name and save new client settings
   *
   * @param deviceName new device name
   */
  private async setDeviceNameSetting(deviceName: string) {
    this._store.dispatch(
      clientSettingsUpdateDeviceNameSettingAction({ deviceName })
    );
  }

  //#region MQTT-handling

  public async subscribeMQTTAddresses(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "subscribeMQTTAddresses()",
      "START"
    );

    if (
      this.deviceModel === BIGDevice.Q896S ||
      this.deviceModel === BIGDevice.Q5
    ) {
      this._addressTocCheck = new Array<Address>();

      this.handleAllNativeInputSibo();
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses()",
        "BEFORE getSiboAddressToSubscribe"
      );

      await Promise.all(this.getSiboAddressToSubscribe());

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses()",
        "AFTER getQ896SAddressSubscribe"
      );

      this.subscribeToAddressChange();
    }
  }

  /**
   * Subscribe all sibo address when device is handled in access control mode
   */
  private getSiboAddressToSubscribe() {
    let arrayPromise: Array<Promise<void>> = [];

    arrayPromise.push(this.subscribeAddressR1PermanentOnOff());
    arrayPromise.push(this.subscribeAddressR2PermanentOnOff());
    arrayPromise.push(this.subscribeAddressR1StairCaseOn());
    arrayPromise.push(this.subscribeAddressR2StairCaseOn());
    arrayPromise.push(this.subscribeAddressR1OnDelay());
    arrayPromise.push(this.subscribeAddressR1OffDelay());
    arrayPromise.push(this.subscribeAddressR2OnDelay());
    arrayPromise.push(this.subscribeAddressR2OffDelay());
    arrayPromise.push(this.subscribeAddressInput1Disable());
    arrayPromise.push(this.subscribeAddressRebootDevice());

    if (this.deviceModel === BIGDevice.Q896S) {
      arrayPromise.push(this.subscribeAddressLedColor());
      arrayPromise.push(this.subscribeAddressInput2Disable());
    }

    return arrayPromise;
  }

  /**
   * Set initial LD state color after login
   *
   * @param siboDeviceInfoState SIBO device state info
   */
  private setInitialColorLedAddress(
    siboDeviceInfoState: SiboDeviceInfoState
  ): void {
    const ledColorAddres: string = this.getVirtualAccessAddress(
      BIGDeviceAddress.LED_COLOR
    );
    const ledColorValue: string =
      siboDeviceInfoState.propertyState[ledColorAddres] || Colors.NONE;

    this.changeColorLedWithString(ledColorValue);
  }

  //#region Handle subscription Relay 2 OFF dalay function on Q896S SIBO device
  /**
   * Subscribe address to reboot device
   */
  private async subscribeAddressRebootDevice(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.REBOOT_DEVICE
      );

      const observableTopic_RebootDevice = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_RebootDevice.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_RebootDevice",
            "REACT"
          );

          const valueReboot: number = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          if (valueReboot === 1) {
            this.rebootDevice();
          }
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeTopic",
        "AppReserved.ADDRESSSUBGROUP_RebootDevice",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription led color on Q896S SIBO device

  private async subscribeAddressLedColor(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.LED_COLOR
      );
      /*
       this._loggerService.log(
         LogLevel.LOG_INFO,
         this._logContext,
         "_mqtt.subscribeAddress -",
         "subscribeAddressLedColor()",
         "START. mqttAddress: " + mqttAddress
       );
       */

      const returnedObservable_LedColor = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = returnedObservable_LedColor.subscribe(
        (message: IMqttMessage) => {
          /*
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_LedColor",
            "REACT"
          );
          */

          const newLEDColor: string = JSON.parse(message.payload.toString())
            .Message.Value.toString()
            .toUpperCase();

          if (newLEDColor && this._LedColor !== newLEDColor) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_mqtt.subscribeAddress -",
              "AppSettings.ADDRESSSUBGROUP_LedColor",
              "CHANGE COLOR: " + newLEDColor
            );
            this._LedColor = newLEDColor;
            this.changeColorLedWithString(this._LedColor);
          }
        }
      );
      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_LedColor",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 1 permanent ON-OFF function on Q896S SIBO device
  private async subscribeAddressR1PermanentOnOff() {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_1_PERMANENT_ON_OFF
      );

      const observableTopic_R1PermanentOnOff = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R1PermanentOnOff.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R1PermanentOnOff",
            "REACT"
          );

          this._R1PermanentOnOff =
            JSON.parse(message.payload.toString()).Message.Value === "1"
              ? true
              : false;

          if (this._R1PermanentOnOff) {
            await this.closeRelay(1, 0, 0);
          } else {
            await this.openRelay(1, 0);
          }
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R1PermanentOnOff",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 2 permanent ON-OFF function on Q896S SIBO device
  private async subscribeAddressR2PermanentOnOff(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_2_PERMANENT_ON_OFF
      );

      const observableTopic_R2PermanentOnOff = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R2PermanentOnOff.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R2PermanentOnOff",
            "REACT"
          );

          this._R2PermanentOnOff =
            JSON.parse(message.payload.toString()).Message.Value === "1"
              ? true
              : false;

          if (this._R2PermanentOnOff) {
            await this.closeRelay(2, 0, 0);
          } else {
            await this.openRelay(2, 0);
          }
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R2PermanentOnOff",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 1 staircase function on Q896S SIBO device
  private async subscribeAddressR1StairCaseOn(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_1_STAIRCASE_ON
      );

      const observableTopic_R1StairCaseOn = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R1StairCaseOn.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R1StairCaseOn",
            "REACT"
          );

          this._R1StairCaseOn = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.closeRelay(1, 0, this._R1StairCaseOn);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R1StairCaseOn",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 2 staircase function on Q896S SIBO device
  private async subscribeAddressR2StairCaseOn(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_2_STAIRCASE_ON
      );

      const observableTopic_R2StairCaseOn = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R2StairCaseOn.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R2StairCaseOn",
            "REACT"
          );

          this._R2StairCaseOn = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.closeRelay(2, 0, this._R2StairCaseOn);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R2StairCaseOn",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region  Handle subscription Relay 1 ON dalay function on Q896S SIBO device
  private async subscribeAddressR1OnDelay(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_1_ON_DELAY
      );

      const observableTopic_R1OnDelay = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R1OnDelay.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R1OnDelay",
            "REACT"
          );

          this._R1OnDelay = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.closeRelay(1, this._R1OnDelay, 0);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R1OnDelay",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 1 OFF dalay function on Q896S SIBO device
  private async subscribeAddressR1OffDelay(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_1_OFF_DELAY
      );

      const observableTopic_R1OffDelay = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R1OffDelay.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R1OffDelay",
            "REACT"
          );

          this._R1OffDelay = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.openRelay(1, this._R1OffDelay);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R1OffDelay",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 2 ON dalay function on Q896S SIBO device
  private async subscribeAddressR2OnDelay(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_2_ON_DELAY
      );

      const observableTopic_R2OnDelay = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R2OnDelay.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R2OnDelay",
            "REACT"
          );

          this._R2OnDelay = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.closeRelay(2, this._R2OnDelay, 0);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeAddress",
        "AppReserved.ADDRESSSUBGROUP_R2OnDelay",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Relay 2 OFF dalay function on Q896S SIBO device
  private async subscribeAddressR2OffDelay(): Promise<void> {
    try {
      const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.RELAY_2_OFF_DELAY
      );

      const observableTopic_R2OffDelay = await this._mqtt.subscribeTopic(
        mqttAddress
      );

      const mqttAddressSubcription = observableTopic_R2OffDelay.subscribe(
        async (message: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_mqtt.subscribeAddress -",
            "ADDRESSSUBGROUP_R2OffDelay",
            "REACT"
          );

          this._R2OffDelay = parseInt(
            JSON.parse(message.payload.toString()).Message.Value
          );

          await this.openRelay(2, this._R2OffDelay);
        }
      );

      await this.checkLocalAddressSubscriptionMap(
        mqttAddress,
        mqttAddressSubcription
      );
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_mqtt.subscribeTopic",
        "AppReserved.ADDRESSSUBGROUP_R2OffDelay",
        "Error: " + exception.message
      );
    }
  }
  //#endregion

  //#region Handle subscription Input 1 dsable function on Q896S SIBO device
  private async subscribeAddressInput1Disable(): Promise<void> {
    // try {
    const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
      BIGDeviceAddress.INPUT_1_DISABLE
    );

    const addresQueryParameter: iAddressSearch = {
      Instanceguid: this._mqttGateway.instanceGUID,
      Address: this.getSimpleVirtualAccessAddress(mqttAddress),
      Name: "",
      Language: "",
    };

    const Input1DisableAddress: Array<Address> =
      (await this._addressService.getAddressesByInstanceGUIDAndAddressAndName(
        addresQueryParameter
      )) as Array<Address>;
    if (Input1DisableAddress !== null) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeAddressInput1Disable()",
        "OK"
      );

      this._input1Disable = Input1DisableAddress[0].lastValue !== "0";

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeAddressInput1Disable()",
        "_input1Disable ORIGINAL VALUE: " + this._input1Disable
      );

      this._addressTocCheck =
        this._addressTocCheck.concat(Input1DisableAddress);
      this._errorInGetAddressINPUT_1_DISABLEapi = false;
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "subscribeAddressInput1Disable()",
        "ERROR IN API CALL!"
      );
      this._errorInGetAddressINPUT_1_DISABLEapi = true;
    }
  }
  //#endregion

  //#region Handle subscription Input 2 dsable function on Q896S SIBO device
  private async subscribeAddressInput2Disable(): Promise<void> {
    //try {
    const mqttAddress = await this.getVirtualAccessInstanceGUIDAddress(
      BIGDeviceAddress.INPUT_2_DISABLE
    );

    const addresQueryParameter: iAddressSearch = {
      Instanceguid: this._mqttGateway.instanceGUID,
      Address: this.getSimpleVirtualAccessAddress(mqttAddress),
      Name: "",
      Language: "",
    };

    const Input2DisableAddress: Array<Address> =
      (await this._addressService.getAddressesByInstanceGUIDAndAddressAndName(
        addresQueryParameter
      )) as Array<Address>;

    if (Input2DisableAddress !== null) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeAddressInput2Disable()",
        "OK"
      );
      this._input2Disable = Input2DisableAddress[0].lastValue !== "0";

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeAddressInput2Disable()",
        "_input2Disable ORIGINAL VALUE: " + this._input2Disable
      );

      this._addressTocCheck =
        this._addressTocCheck.concat(Input2DisableAddress);
      this._errorInGetAddressINPUT_2_DISABLEapi = false;
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "subscribeAddressInput2Disable()",
        "ERROR IN API CALL!"
      );
      this._errorInGetAddressINPUT_2_DISABLEapi = true;
    }
  }
  //#endregion

  /**
   * Handle Subscription to mqtt value change inside address service; in this service, this is applied ONLY to mqtt addresses we need
   * to know the last value of at Subscription moment, since to this end we need to handle "Address" objects taken from the server.
   * These addresses are:
   *  +  BIGDeviceAddress.INPUT_1_DISABLE
   *  +  BIGDeviceAddress.INPUT_2_DISABLE
   */
  private subscribeToAddressChange() {
    if (!this._subscriptionAddressUpdate) {
      this._subscriptionAddressUpdate = this._addressService.addressUpdate
        .pipe(filter((address) => address !== null))
        .pipe(filter((address) => this.checkIfAddressUpdateUseful(address)))
        .subscribe(async (address: Address) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_addressService",
            "addressUpdate",
            "Address update for: " +
            address.instanceGUID +
            "_" +
            address.address
          );

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_addressService",
            "addressUpdate",
            "REACT - value:" + address.lastValue
          );

          switch (address.address) {
            case this.getSimpleVirtualAccessAddress(
              await this.getVirtualAccessInstanceGUIDAddress(
                BIGDeviceAddress.INPUT_1_DISABLE
              )
            ):
              this._input1Disable = address.lastValue !== "0";
              break;

            case this.getSimpleVirtualAccessAddress(
              await this.getVirtualAccessInstanceGUIDAddress(
                BIGDeviceAddress.INPUT_2_DISABLE
              )
            ):
              this._input2Disable = address.lastValue !== "0";
              break;

            default:
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                "_addressService",
                "addressUpdate",
                "Error in switch. Address: " + address.address
              );
              break;
          }

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_addressService",
            "addressUpdate",
            "REACT - current _input1Disable:" +
            this._input1Disable +
            " | current _input2Disable: " +
            this._input2Disable
          );
        });
    }
  }

  /**
   * This method check if the data emitted on "addressUpdate" subject refers to some locally used Address
   * @param addressToCheck Address object emitted on "addressUpdate" subject
   * @returns TRUE if "addressToCheck" is an mqtt-address used in this service, FALSE otherwise.
   */
  private checkIfAddressUpdateUseful(addressToCheck: Address): boolean {
    let result: boolean = false;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_addressService",
      "checkIfAddressUpdateUseful",
      "AddressTocCkeck ID: " + addressToCheck.id
    );
    for (const add of this._addressTocCheck) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_addressService",
        "checkIfAddressUpdateUseful",
        "Address in list ID: " + add.id
      );
    }

    if (
      this._addressTocCheck.find(
        (address: Address) => address.id === addressToCheck.id
      )
    ) {
      result = true;
    }

    return result;
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Q896S Native Input handling
  /**
   * Native function to handle input generic changes
   *
   */
  private handleAllNativeInputSibo(): void {
    this.handleNativeSiboInput1();

    if (this.deviceModel === BIGDevice.Q896S) {
      this.handleNativeSiboInput2();
    }
  }

  /**
   * Handle native Sibo input 1
   */
  private handleNativeSiboInput1(): void {
    this._siboHandler.EnableInput1();

    this._siboHandler
      .OnInput1Change()
      .pipe(
        filter(() => !this._input1Disable),
        distinctUntilKeyChanged("state"),
        map((siboObject) => siboObject.state),
        map((state) => this.getInputStateAsInt(state)),
        tap(() => {
          this.checkIfDisableInputAPIToBeRecalled();
        })
      )
      .subscribe(async (state) => {
        const virtualAddress = await this.getVirtualAccessInstanceGUIDAddress(
          BIGDeviceAddress.INPUT_1_BIT
        );

        this._mqtt.publishAddress(virtualAddress, state.toString());
      });
  }

  /**
   * Handle native Sibo input 2
   */
  private handleNativeSiboInput2(): void {
    this._siboHandler.EnableInput2();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleAllNativeInputQ896S()",
      "START"
    );

    this._siboHandler
      .OnInput2Change()
      .pipe(
        filter(() => !this._input2Disable),
        distinctUntilKeyChanged("state"),
        map((siboObject) => siboObject.state),
        map((state) => this.getInputStateAsInt(state)),
        tap(() => {
          this.checkIfDisableInputAPIToBeRecalled();
        })
      )
      .subscribe(async (state) => {
        const virtualAddress = await this.getVirtualAccessInstanceGUIDAddress(
          BIGDeviceAddress.INPUT_2_BIT
        );

        this._mqtt.publishAddress(virtualAddress, state.toString());
      });
  }

  /**
   * Convert plugin input state as number.
   * Contact is normally open:
   * - When effective state is 0: contact is close then return 1
   * - When effective state is 1: contact is open then return 0
   *
   * @param state Input state
   * @returns
   */
  private getInputStateAsInt(state: string | number): number {
    return state.toString().toUpperCase() === "TRUE" || state === 0 ? 1 : 0;
  }

  /**
   * Check if input 1 or input 2 API disable must be recalled
   */
  private async checkIfDisableInputAPIToBeRecalled(): Promise<void> {
    if (!this._isCheckingApisInProgress) {
      this._isCheckingApisInProgress = true;

      while (
        this._errorInGetAddressINPUT_1_DISABLEapi ||
        this._errorInGetAddressINPUT_2_DISABLEapi
      ) {
        if (this._errorInGetAddressINPUT_1_DISABLEapi) {
          await this.subscribeAddressInput1Disable();
        }

        if (
          this._errorInGetAddressINPUT_2_DISABLEapi &&
          this.deviceModel === BIGDevice.Q896S
        ) {
          await this.subscribeAddressInput2Disable();
        }
      }
      this._isCheckingApisInProgress = false;
    }
  }

  //#endregion

  /**
   * Subsribe to public Observable "clientSettings" of "settings.service", which propagates the info about the general app
   * -related settings as defined for this device; it will also emit each time these settings are changed.
   */
  private subscribeSettingServiceData(): void {
    this._selectClientSettingsUserSub = this._store
      .select(clientSettings)
      .pipe(
        filter((clientSettings) => clientSettings !== null),
        tap((clientSettings: ClientSettings) => {
          //If native adapter is set refresh current configuration
          if (this._input1NativeAdapter) {
            this._input1NativeAdapter.inputConfiguration =
              clientSettings.input1Configuration;
          }

          if (this._input2NativeAdapter) {
            this._input2NativeAdapter.inputConfiguration =
              clientSettings.input2Configuration;
          }
        })
      )
      .subscribe((clientSettings: ClientSettings) => {
        this.setRelayHandlerProperties(clientSettings);
        this._currentClientSetting = clientSettings;
      });
  }

  /**
   * Set-up properties (both the ones in "ClientSettings" AND the private ones of this service) handling settings related to single relays; the number of relays is variable, so we must do it here.
   * These properties are implemented as Arrays, whose lenght is defined by the leght of the "BIGDeviceRelays****" enum formalizing the relay set present on the device.
   * Each relay's item contains data referring to a single existing relay.
   *
   * ATTENTION: Since the first key of a "BIGDeviceRelays****" enum represents the "UNDEFINED"/"NO_RELAY" situation, the first significative item in these array shall be
   * the SECOND one (index= 1, as in "Relay 1").
   * @param clientSettings
   */
  private setRelayHandlerProperties(clientSettings: ClientSettings): void {
    let enablersArray: Array<boolean> = new Array<boolean>();
    let relayTimeLimitsArray: Array<number> = new Array<number>();

    this._relayIsUnderSafety = new Array<boolean>();
    this._lastCommandToRelay = new Array<number>();
    this._safetyTimers = new Array<any>();

    //SOLE CASE UNTIL NOW: ("MYTO" TOUCH)
    for (const relay of EnumStringConverter.genericEnumtoArray<BIGDeviceRelaysQ896S>(
      this._enumDeviceRelays
    )) {
      enablersArray.push(false);
      relayTimeLimitsArray.push(5);
      this._relayIsUnderSafety.push(false);
      this._lastCommandToRelay.push(null);
      this._safetyTimers.push(null);
    }

    //enablersArray.shift();
    //relayTimeLimitsArray.shift();
    if (!clientSettings.isRelayTimeLimitEnableArray) {
      this._store.dispatch(
        clientSettingsUpdateRelayTimeLimitEnablersListAction({
          enablersTimeLimitList: enablersArray,
        })
      );

      this._store.dispatch(
        clientSettingsUpdateMaximumRelayActivationTimeSettingListAction({
          maximumTimeLimitList: relayTimeLimitsArray,
        })
      );
    }
  }

  /**
   * This method handle the EVENTUAL application of "safety" maximum time limit to the device-integrated relays activation.
   * This elaboration happen each time a command (both OPEN and CLOSE) is given to a peculiar relay.
   * @param relayOperationKind Enum, define the peculiar command (either OPEN or CLOSE) given to a certain relay.
   * @param configuration Configuration to be passed with the peculiar "relay-handling" order; it should extends the inteface "CommandConfiguration", so that it shall contain "relayNumber : number" property
   * @returns
   */
  private async handleSafetyMaximumTimeLimit<T extends CommandConfiguration>(
    relayOperationKind: BIGDeviceRelayOperationKind,
    configuration: T
  ): Promise<ReponseObject> {
    let result: any;

    const safetyLimitDuration: number =
      this.checkSafetyLimitApplication(configuration);

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleSafetyMaximumTimeLimit()",
      "START. configuration: " +
      JSON.stringify(configuration) +
      " safetyDuration " +
      safetyLimitDuration
    );

    //CASE: We are handling a "CLOSE" command AND the "maximum time limit" is enabled (property "safetyLimitDuration" is defined)
    if (
      relayOperationKind === BIGDeviceRelayOperationKind.CLOSE &&
      safetyLimitDuration
    ) {
      result = this.applySafetyMaximumTimeLimit(
        configuration as unknown as RelayCloseConfiguration,
        safetyLimitDuration
      );
    } else {
      //CASE: We are handling a "OPEN" command OR the "maximum time limit" is UN-enabled.
      result = this.noSafetyMaximumTimeLimit(relayOperationKind, configuration);
    }
    //this._lastRelayCommand = relayOperationKind;

    return result;
  }

  /**
   * This method checks if the relay-handling command (OPEN or CLOSE) currently received is directed to a relay with the "safety limit" enabled.
   * IF SO, it returns the extension of the associated safety-time-limit. OTHERWISE, it returns NULL.
   * @param closeConfiguration The configuration passed with the command; it is used to define the relay the command targets.
   * @returns The extension of the safety-time-limit associated to the target-relay if its "safety limit" is enabled; otherwise, NULL.
   */
  private checkSafetyLimitApplication(
    closeConfiguration: CommandConfiguration
  ): number {
    let result: number = null;

    if (
      this._currentClientSetting.isRelayTimeLimitEnableArray[
      closeConfiguration.relayNumber
      ]
    ) {
      result =
        this._currentClientSetting.relayTimeLimitArray[
        closeConfiguration.relayNumber
        ];
    }
    return result;
  }

  /**
   * This method handles the cases where there is NO need to apply the "automatic opening" to the target relay.
   * These cases includes:
   *
   *  * The relay-handling command given is "OPEN". In this case, the timer regulating the "automatic closure" of the realy (if present) is stopped, before the command is enacted.
   *  * The "safety limit" associated to the target-relay is currently UN-enable. In this case,
   * @param relayOperationKind Enum, define the peculiar command (either OPEN or CLOSE) given to the target-relay.
   * @param configuration The configuration passed with the command; it is used to define the relay the command targets.
   * @returns
   */
  private async noSafetyMaximumTimeLimit(
    relayOperationKind: BIGDeviceRelayOperationKind,
    configuration: CommandConfiguration
  ): Promise<ReponseObject> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "noSafetyMaximumTimeLimit()",
      "Last command array" + JSON.stringify(this._lastCommandToRelay)
    );

    switch (relayOperationKind) {
      case BIGDeviceRelayOperationKind.CLOSE:
        this._lastCommandToRelay[configuration.relayNumber] =
          BIGDeviceRelayOperationKind.CLOSE;

        return await this._siboHandler.CloseRelay(
          configuration as RelayCloseConfiguration
        );

        break;

      case BIGDeviceRelayOperationKind.OPEN:
        if (
          this._lastCommandToRelay[configuration.relayNumber] ===
          BIGDeviceRelayOperationKind.CLOSE &&
          this._relayIsUnderSafety[configuration.relayNumber]
        ) {
          clearTimeout(this._safetyTimers[configuration.relayNumber]);
          this._safetyTimers[configuration.relayNumber] = null;

          this._relayIsUnderSafety[configuration.relayNumber] = false;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "noSafetyMaximumTimeLimit()",
            "STOPPING PEVIOUS TIMER"
          );
        }

        this._lastCommandToRelay[configuration.relayNumber] =
          BIGDeviceRelayOperationKind.OPEN;

        return await this._siboHandler.OpenRelay(
          configuration as RelayOpenConfiguration
        );

        break;
    }
  }

  /**
   * This method handles the cases where we should apply the "automatic opening" to the target relay.
   *
   * If the target-relay has already received a "CLOSE" command AND the resulting "automatic opening timer" is yet to expire, the time is reset.
   *
   * If the "closeConfiguration" of the "CLOSE" command issued contains a significant property "onDelay", the "automatic opening timer" duration is consequently extended.
   *
   * If the "closeConfiguration" of the "CLOSE" command issued contains a significant property "stairCaseDuration", the "automatic opening timer" duration is set to the MINIMUM value between that property
   * and "safetyLimitDuration" parameter.
   *
   * @param closeConfiguration The configuration passed with the command.
   * @param safetyLimitDuration The duration of  the "automatic opening timer", as defined through the clientSettings's "relayTimeLimitArray" property and setted by the user in "app-options" page.
   * @returns
   */
  private async applySafetyMaximumTimeLimit(
    closeConfiguration: RelayCloseConfiguration,
    safetyLimitDuration: number
  ): Promise<ReponseObject> {
    if (
      this._lastCommandToRelay[closeConfiguration.relayNumber] ===
      BIGDeviceRelayOperationKind.CLOSE &&
      this._relayIsUnderSafety[closeConfiguration.relayNumber]
    ) {
      clearTimeout(this._safetyTimers[closeConfiguration.relayNumber]);
      this._safetyTimers[closeConfiguration.relayNumber] = null;
    }

    const formattedRelayTimeLimit: number = safetyLimitDuration * 1000;
    const effectiveTimeRelayLimit: number =
      closeConfiguration.onDelay > 0
        ? formattedRelayTimeLimit + closeConfiguration.onDelay
        : formattedRelayTimeLimit;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "applySafetyMaximumTimeLimit()",
      "effectiveTimeRelayLimit: " + effectiveTimeRelayLimit
    );

    if (closeConfiguration?.stairCaseDuration <= 0) {
      this._relayIsUnderSafety[closeConfiguration.relayNumber] = true;

      this._safetyTimers[closeConfiguration.relayNumber] = setTimeout(() => {
        const openConfig: RelayOpenConfiguration = {
          relayNumber: closeConfiguration.relayNumber,
          offDelay: 0,
        };

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "applySafetyMaximumTimeLimit()",
          "SAFETY OVERRIDE! Relay: " + closeConfiguration.relayNumber
        );

        this._siboHandler.OpenRelay(openConfig);

        this._lastCommandToRelay[closeConfiguration.relayNumber] =
          BIGDeviceRelayOperationKind.OPEN;
        this._relayIsUnderSafety[closeConfiguration.relayNumber] = false;
      }, effectiveTimeRelayLimit);
    } else {
      if (closeConfiguration.stairCaseDuration > effectiveTimeRelayLimit) {
        closeConfiguration.stairCaseDuration = effectiveTimeRelayLimit;
      }
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "applySafetyMaximumTimeLimit()",
      "FINAL closeConfiguration: " + JSON.stringify(closeConfiguration)
    );

    this._lastCommandToRelay[closeConfiguration.relayNumber] =
      BIGDeviceRelayOperationKind.CLOSE;

    return await this._siboHandler.CloseRelay(closeConfiguration);
  }

  /**
   * Handle clearing of "under safety" relays at logging-out/closure of app.
   */
  private clearAllRelay() {
    for (
      let timerIndex = 1;
      timerIndex < this._safetyTimers?.length;
      timerIndex++
    ) {
      clearTimeout(this._safetyTimers[timerIndex]);
      this._safetyTimers[timerIndex] = null;
    }

    for (
      let relayIndex = 1;
      relayIndex < this._lastCommandToRelay.length;
      relayIndex++
    ) {
      if (this._currentClientSetting.isRelayTimeLimitEnableArray[relayIndex]) {
        this._siboHandler.OpenRelay({ relayNumber: relayIndex, offDelay: 0 });

        this._lastCommandToRelay[relayIndex] = BIGDeviceRelayOperationKind.OPEN;
        this._relayIsUnderSafety[relayIndex] = false;
      }
    }
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectClientSettingsUserSub?.unsubscribe();
    this._selectedPlantSubscription?.unsubscribe();

    //this._selectGatewayListSubscription?.unsubscribe();
  }
  //#endregion

  /**
   * Disable input 1 and 2 trasmission time.
   * It clear internal plugin
   */

  private disableInputTransmission(): void {
    this._siboHandler.DisableInput1();
    this._siboHandler.DisableInput2();
  }

  /**
   * Reset input variable
   */
  private resetInputVariable(): void {
    this._input1Disable = true;
    this._input2Disable = true;
  }

  /**
   * Unsubscribe to device input changes
   */
  private unsubscribeToInputChanges(): void {
    this._input1ChangeSubscription?.unsubscribe();
    this._input2ChangeSubscription?.unsubscribe();
  }

  /**
   * Load current lant
   */
  private loadCurrentPlant(): void {
    this._selectedPlantSubscription = this._store
      .select(selectedPlant)
      .pipe(filter((currentPlant: Plant) => currentPlant !== null))
      .subscribe((currentPlant: Plant) => {
        this._currentPlant = currentPlant;
      });
  }

  /**
   * Dispatch current operating mode to server
   */
  private async dispatchOperatingMode(): Promise<void> {
    const virtualAddressOperatingMode =
      await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.OPERATING_MODE
      );

    /** Publish current plant operating mode */
    this._mqtt.publishAddress(
      virtualAddressOperatingMode,
      this._currentPlant.operativeMode.toString()
    );
  }

  /**
   * Dispatch device descriptor to server
   */
  private async dispatchCurrentDeviceModel(): Promise<void> {
    const virtualAddressDeviceDescriptor =
      await this.getVirtualAccessInstanceGUIDAddress(
        BIGDeviceAddress.DEVICE_DESCRIPTOR
      );

    /** Publish current device descriptor as enum number */
    this._mqtt.publishAddress(
      virtualAddressDeviceDescriptor,
      this.deviceModel.toString()
    );
  }

  /**
   * Get manufacturer system by device type
   */
  private getManufacturerByDeviceType(
    deviceType: BIGDeviceType
  ): ManufacturerSystem {
    let manufacturer = ManufacturerSystem.UNKNOWN;
    if (deviceType == BIGDeviceType.ABB_MILLENIUM) {
      manufacturer = ManufacturerSystem.ABB_MILLENIUM;
    }
    if (deviceType == BIGDeviceType.BIG_MIFARE) {
      manufacturer = ManufacturerSystem.BIG_VIRTUAL_MIFARE;
    }
    if (deviceType == BIGDeviceType.VIMAR_WELL_CONTACT_PLUS) {
      manufacturer = ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS;
    }
    return manufacturer;
  }

  /**
   * Insert entry into blackbox
   */
  public insertEntryBlackBox(block: GenericTag, deviceType: BIGDeviceType) {
    let entryBlackBox = new BlackBoxEntry();
    entryBlackBox.instanceGuid = uuidv4();
    entryBlackBox.typeAction = BlackBoxDeviceEvent.READ_TAG;
    entryBlackBox.extraInfo = new ExtraInfoBlackBox();
    entryBlackBox.extraInfo.manufacturer =
      this.getManufacturerByDeviceType(deviceType);
    entryBlackBox.extraInfo.blockReaded =
      block?.UID +
      "|" +
      block?.CardNumber +
      "|" +
      block?.ExpirationDate +
      "|" +
      block?.PlantCode;
    entryBlackBox.dateTime = new Date().toISOString();

    this._store.dispatch(
      blackBoxAddEntry({
        insertEntry: entryBlackBox,
        numberEvents: this._currentClientSetting.numberEventsBlackBox,
      })
    );
  }

  /**
   * Recover keyA from serverSettings
   *
   * @returns keyA
   */
  private calculateKeyA(): string {
    let uppKey = (
      this._serverSetting["CardAccessControlUpperKeyA"] * 1
    ).toString(16);
    let midKey = (this._serverSetting["CardAccessControlMidKeyA"] * 1).toString(
      16
    );
    let lowKey = (
      this._serverSetting["CardAccessControlLowerKeyA"] * 1
    ).toString(16);
    return (uppKey + midKey + lowKey).toString().toUpperCase();
  }

  /**
   * This method is used to check if If enough time has passed from the invocation of the Sibo-plugin reading 
   * method (readBlock()/readUID()) that the next reading can be considered "admissible". 
   * If it is the case, the calling method proceed with the returned read string. Otherwise, the returned value is ignored
   * and the reading method is called again.
   */
  private prematureReleaseTimer(callStamp: string): void {

    const correctReleaseTimer = interval(800);

    correctReleaseTimer.pipe(
      take(1)
    ).subscribe(() => {
      this._isNFCReadyToRelease = true;

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "prematureReleaseTimer()",
        "TRIGGERED! is NFC Ready To Release: " +
          this._isNFCReadyToRelease +
          " | STAMP: " +
          callStamp,
      );
    });

  }


}

/**
 * This interface is used in order to apply a necessary constraint to generic-type parameters which should be EITHER "RelayCloseConfiguration" OR "RelayOpenConfiguration".
 */
export interface CommandConfiguration {
  relayNumber: number;
}
